import "./FileDialog.scss";

import data from "./data";
import icons from "./icons";
import FileInfo from "./FileInfo";
import splitFileList from "./helpers/splitFileList";
import formatBytes from "./helpers/formatBytes";


/**
 * A dialog to display and capture information for files being uploaded.
 */
export default class FileDialog {
    /**
     * Create a new instance of the class.
     * @param {Form} parent The parent `Form` for this instance.
     */
    constructor( parent ) {
        // bind the context within internal listeners
        this.onCancelOrCloseClicked = this.onCancelOrCloseClicked.bind( this );
        this.onTrapFocusKeyDown = this.onTrapFocusKeyDown.bind( this );
        this.onUploadClicked = this.onUploadClicked.bind( this );

        /**
         * The parent `Form` for this instance.
         * @type {Form}
         * @readonly
         */
        this.parentForm = parent;

        /**
         * The root/outer element for the dialog.
         * @type {HTMLDivElement|null}
         * @readonly
         */
        this.element = this.createElement();

        /**
         * The body element for the dialog.
         * @type {HTMLDivElement|null}
         * @readonly
         */
        this.body = this.element.querySelector( ".fg-feu-dialog-body" );

        /**
         *
         * @type {HTMLDivElement|null}
         * @readonly
         */
        this.loader = null;

        /**
         * The close button for the dialog that appears within the header.
         * @type {HTMLButtonElement}
         * @readonly
         */
        this.buttonClose  = this.element.querySelector( ".fg-feu-dialog-close" );
        this.buttonClose?.addEventListener( "click", this.onCancelOrCloseClicked );

        /**
         * The cancel button for the dialog that appears within the footer.
         * @type {HTMLButtonElement}
         * @readonly
         */
        this.buttonCancel  = this.element.querySelector( ".fg-feu-dialog-cancel" );
        this.buttonCancel?.addEventListener( "click", this.onCancelOrCloseClicked );

        /**
         * The upload button for the dialog that appears within the footer.
         * @type {HTMLButtonElement}
         * @readonly
         */
        this.buttonUpload  = this.element.querySelector( ".fg-feu-dialog-upload" );
        if ( typeof data.rest.endpoint === 'string' && typeof data.rest.nonce === 'string' ) {
            this.buttonUpload?.addEventListener( "click", this.onUploadClicked );
        }

        /**
         * A map of HTMLDivElements to their associated FileInfo class instance.
         * @type {Map<HTMLDivElement, FileInfo>}
         * @readonly
         */
        this.fileInfos = new Map();

        /**
         * Tracks first-only field values for syncing
         * @type {Map<string, string>}
         * @private 
         */
        this._firstOnlyValues = new Map();

        // Bind new method
        this.syncFirstOnlyFields = this.syncFirstOnlyFields.bind(this);
    }

    /**
     * Create the root/outer element for the dialog.
     * @return {HTMLDivElement}
     */
    createElement() {
        const element = document.createElement( "div" );
        element.classList.add( "fg-feu-dialog" );
        element.role = "dialog";
        element.innerHTML = `<article class="fg-feu-dialog-inner">
    <header class="fg-feu-dialog-header">
        <h2 class="fg-feu-dialog-title">${ this.parentForm.i18n.dialogTitle }</h2>
        <button class="fg-feu-header-button fg-feu-dialog-close" aria-label="${ this.parentForm.i18n.dialogClose }">
            ${ icons.cross }
        </button>
    </header>
    <div class="fg-feu-dialog-body"></div>
    <footer class="fg-feu-dialog-footer">
        <button type="submit" class="fg-feu-button fg-feu-primary-button fg-feu-dialog-upload" name="foogallery_image_upload">${ this.parentForm.i18n.dialogUpload }</button>
        <button type="button" class="fg-feu-button fg-feu-dialog-cancel">${ this.parentForm.i18n.dialogCancel }</button>
    </footer>
</article>`;
        return element;
    }

    createResultNotice( result ) {
        const partial = result.errored > 0 || result.skipped > 0;
        let icon, className;
        if ( result.success === true ) {
            icon = partial ? icons.warn : icons.success;
            className = partial ? "fg-feu-warn" : "fg-feu-success";
        } else {
            icon = icons.error;
            className = "fg-feu-error";
        }
        const element = this.createNotice( result.message, className, icon );
        if ( partial ) {
            const details = this.createResultDetails( result );
            element.append( details );
        }
        return element;
    }

    createResultDetails( result ) {
        const details = document.createElement( "div" );
        details.classList.add( "fg-feu-result-details" );
        const successes = result.files.filter( file => file.status === "succeeded" );
        if ( successes.length > 0 ) {
            const succeeded = this.createResultFilesList( successes, this.parentForm.i18n.resultSucceeded, "fg-feu-succeeded" );
            details.append( succeeded );
        }
        const errors = result.files.filter( file => file.status === "errored" );
        if ( errors.length > 0 ) {
            const errored = this.createResultFilesList( errors, this.parentForm.i18n.resultErrored, "fg-feu-errored" );
            details.append( errored );
        }
        const skips = result.files.filter( file => file.status === "skipped" );
        if ( skips.length > 0 ) {
            const skipped = this.createResultFilesList( skips, this.parentForm.i18n.resultSkipped, "fg-feu-skipped" );
            details.append( skipped );
        }
        return details;
    }

    /**
     *
     * @param {FileResult[]} files
     * @param {string} title
     * @param {string} className
     * @param {string} [icon]
     * @return {HTMLDivElement}
     */
    createResultFilesList( files, title, className, icon = '' ) {
        const element = document.createElement( "div" );
        element.classList.add( "fg-feu-result-files", className );

        const header = document.createElement( "span" );
        header.classList.add( "fg-feu-result-files-header" );
        header.innerHTML = typeof icon === 'string' && icon.length > 0 ? `${ icon }${ title }` : title;
        element.append( header );

        const list = document.createElement( "ul" );
        list.classList.add( "fg-feu-result-files-list" );
        for ( const file of files ) {
            const li = document.createElement( "li" );
            li.textContent = `${ file.name }` + ( file.message.length > 0 ? ` - ${ file.message }` : '' );
            list.append( li );
        }
        element.append( list );

        return element;
    }

    /**
     * Open the dialog.
     */
    open() {
        this.checkHasFields();
        this.checkIsLast();
        this.populate();
        document.documentElement.classList.add( "fg-feu-dialog-open" );
        this.parentForm.element.append( this.element );
        this.trapFocus();
        this.setInitialFocus();
    }

    /**
     * Close the dialog.
     */
    close() {
        this.buttonUpload.classList.remove( "fg-feu-hidden" );
        this.buttonUpload.disabled = false;
        this.buttonCancel.textContent = this.parentForm.i18n.dialogCancel;
        this.element.classList.remove('fg-feu-validation-attempted');
        this.empty();
        this.parentForm.reset();
        this.releaseFocus();
        document.documentElement.classList.remove( "fg-feu-dialog-open" );
        this.element.remove();
    }

    /**
     * Sync values from first-only fields to all files
     * @param {string} field Field name
     * @param {string} value Field value 
     */
    syncFirstOnlyFields(field, value) {
        this._firstOnlyValues.set(field, value);
        // Find all files except first one
        const fileInfos = Array.from(this.fileInfos.values());
        if (fileInfos.length <= 1) return;

        fileInfos.slice(1).forEach(fileInfo => {
            const input = fileInfo.element.querySelector(`input[name="${field}"]`);
            if (input) input.value = value;
        });
    }

    /**
     * Populate the dialog with `FileInfo` elements for each `File` in the `Form`.
     */
    populate() {
        this.empty();
        const [ passedSize, tooLarge ] = splitFileList( this.parentForm.fileInput.files, file => this.parentForm.maxSize === 0 || file.size < this.parentForm.maxSize );
        const [ passedTypes, wrongTypes ] = splitFileList( passedSize, file => this.parentForm.isAllowedFileType( file ) );
        const [ files, tooMany ] = splitFileList( passedTypes, ( file, i ) => this.parentForm.maxImages === 0 || i < this.parentForm.maxImages );

        const notice = this.createOpenNotice( files, tooLarge, tooMany, wrongTypes );
        if ( notice instanceof HTMLElement ) this.body.append( notice );

        this.parentForm.fileInput.files = files;
        this.checkFilesChanged();

        for ( const file of this.parentForm.files ) {
            const fi = new FileInfo( this, file );
            this.body.append( fi.element );
            this.fileInfos.set( fi.element, fi );
        }
    }

    createNotice( message, className, icon = '', sticky = false ) {
        const notice = document.createElement( "div" );
        notice.classList.add( "fg-feu-notice" );
        notice.classList.toggle( "fg-feu-notice-sticky", sticky );
        if ( typeof className === 'string' && className.length > 0 ) {
            notice.classList.add( className );
        }
        if ( Array.isArray( className ) && className.length > 0 ) {
            notice.classList.add( ...className );
        }
        const msg = document.createElement( "span" );
        msg.classList.add( "fg-feu-message" );
        if ( typeof icon === 'string' && icon.length > 0 ) {
            msg.innerHTML = `${ icon }${ message }`;
        } else {
            msg.innerHTML = message;
        }
        notice.append( msg );
        return notice;
    }

    createOpenNotice( files, tooLarge, tooMany, wrongTypes ) {
        if ( tooLarge.length > 0 || tooMany.length > 0 || wrongTypes.length > 0 ) {
            const messages = [];
            if ( tooLarge.length > 0 ) {
                let message;
                if ( files.length === 0 ) {
                    message = tooLarge.length === 1 ? this.parentForm.i18n.fileAllTooLarge : this.parentForm.i18n.fileAllTooLargePlural;
                } else {
                    message = tooLarge.length === 1 ? this.parentForm.i18n.fileSomeTooLarge : this.parentForm.i18n.fileSomeTooLargePlural;
                }
                messages.push( message.replace( "%s", formatBytes( this.parentForm.maxSize ) ) );
            }
            if ( tooMany.length > 0 ) {
                const message = this.parentForm.maxImages === 1 ? this.parentForm.i18n.fileTooMany : this.parentForm.i18n.fileTooManyPlural;
                messages.push( message.replace( "%d", String( this.parentForm.maxImages ) ) );
            }
            if ( wrongTypes.length > 0 ) {
                let message;
                if ( wrongTypes.length === 1 ) {
                    const extension = wrongTypes[0].name.split( "." ).pop()?.toLowerCase();
                    message = this.parentForm.i18n.fileTypeNotAllowed.replace( "%s", extension );
                } else {
                    message = this.parentForm.i18n.fileTypeNotAllowedPlural;
                }
                message = message.replace( "%s", this.parentForm.allowedTypes.join( ", " ) );
                messages.push( message );
            }
            if ( messages.length > 0 ) {
                return this.createNotice( messages.join( " " ), "fg-feu-warn", icons.warn, files.length > 0 );
            }
        }
        return null;
    }

    /**
     * Empty the dialog removing all `FileInfo` elements and leaving the `body` empty.
     */
    empty(){
        for ( const fi of this.fileInfos.values() ) {
            fi.remove();
        }
        this.fileInfos.clear();
        this.body.replaceChildren();
    }

    showLoader() {
        if ( !( this.loader instanceof HTMLDivElement ) ) {
            this.loader = this.createNotice( this.parentForm.i18n.restUploading, [ "fg-feu-info", "fg-feu-loader" ], icons.loader );
        }
        this.body.replaceChildren( this.loader );
        this.buttonUpload.disabled = true;
        this.buttonCancel.disabled = true;
        this.buttonClose.disabled = true;
    }

    /**
     * Hide the loader and restore the form for editing
     */
    hideLoader() {
        // Remove loader and re-populate the form
        this.populate();
        this.buttonUpload.disabled = false;
        this.buttonCancel.disabled = false;
        this.buttonClose.disabled = false;
    }

    /**
     *
     * @param {UploadResponse} result
     */
    showResult( result ) {
        const element = this.createResultNotice( result );
        this.body.replaceChildren( element );
        this.buttonUpload.classList.add( "fg-feu-hidden" );
        this.buttonClose.disabled = false;
        this.buttonCancel.disabled = false;
        this.buttonCancel.textContent = this.parentForm.i18n.dialogClose;
    }

    checkFilesChanged() {
        this.checkIsLast();
        this.buttonUpload.disabled = !this.parentForm.hasAnyFiles;
    }

    /**
     * Check if there is only a single file to display and toggle the appropriate CSS class.
     */
    checkIsLast() {
        this.element.classList.toggle( "fg-feu-single", this.parentForm.hasSingleFile );
    }

    /**
     * Check if there are no fields to display and toggle the appropriate CSS class.
     */
    checkHasFields() {
        this.element.classList.toggle( "fg-feu-no-fields", !this.parentForm.hasFields );
    }

    /**
     * Handle the click event for both the cancel and close buttons.
     * @param {MouseEvent} event
     */
    onCancelOrCloseClicked( event ) {
        event.preventDefault();
        this.close();
    }

    async onUploadClicked( event ) {
        event.preventDefault();
        
        // Mark that user has attempted to submit (for CSS validation styling)
        this.element.classList.add('fg-feu-validation-attempted');
        
        // Validate required fields
        const validationResult = this.validateRequiredFields();
        if (!validationResult.valid) {
            // Show validation error
            this.showValidationError(validationResult.message);
            return;
        }
        
        const formData = new FormData( this.parentForm.element );
        this.showLoader();
        const result = await this.parentForm.upload( formData );
        
        // Check if server-side validation failed
        if (result.errored > 0 && result.succeeded === 0) {
            // Check if any error is a required field validation error
            const hasValidationError = result.files.some(file => 
                file.status === 'errored' && 
                file.message && 
                file.message.includes('Required field')
            );
            
            if (hasValidationError) {
                // Show validation error and keep form open for editing
                this.hideLoader();
                const errorMessages = result.files
                    .filter(file => file.status === 'errored')
                    .map(file => file.message)
                    .join(', ');
                this.showValidationError(errorMessages);
                return;
            }
        }
        
        // Show normal result (success or other errors)
        this.showResult( result );
    }

    /**
     * Validate that all required fields are filled
     * @return {{valid: boolean, message: string}}
     */
    validateRequiredFields() {
        if (this.parentForm.requiredFields.length === 0) {
            return { valid: true, message: '' };
        }

        const invalidFields = [];
        const requiredInputs = this.element.querySelectorAll('input[required], textarea[required], select[required]');
        
        for (const input of requiredInputs) {
            if (!input.checkValidity()) {
                // Get the field name from the label
                const label = input.closest('.fg-feu-file-field');
                const fieldName = label?.querySelector('.fg-feu-file-field-name')?.textContent?.replace(' *', '') || input.name;
                invalidFields.push(fieldName);
            }
        }

        if (invalidFields.length > 0) {
            const message = data.i18n.requiredFieldsError || 'Please fill in all required fields (marked with *).';
            return { valid: false, message: message };
        }

        return { valid: true, message: '' };
    }

    /**
     * Show validation error message
     * @param {string} message
     */
    showValidationError(message) {
        // Remove any existing validation notices
        const existingNotice = this.body.querySelector('.fg-feu-validation-error');
        if (existingNotice) {
            existingNotice.remove();
        }

        // Create and insert validation error notice at the top
        const notice = this.createNotice(message, ['fg-feu-error', 'fg-feu-validation-error'], icons.error, true);
        this.body.insertBefore(notice, this.body.firstChild);
        
        // Scroll to top of dialog to show error
        this.body.scrollTop = 0;
        
        // Focus on first invalid field
        const firstInvalid = this.element.querySelector('input[required]:invalid, textarea[required]:invalid, select[required]:invalid');
        if (firstInvalid instanceof HTMLElement) {
            firstInvalid.focus();
        }
    }

    //region Focus Related

    /**
     * Sets the initial focus of the dialog to be either the first field input or the upload button.
     */
    setInitialFocus() {
        if ( this.parentForm.hasFields ) {
            const first = this.body.querySelector( ".fg-feu-file-field-input" );
            if ( first instanceof HTMLElement ) first.focus();
        } else {
            this.buttonUpload.focus();
        }
    }

    /**
     * The element that had focus before the dialog.
     * @type {Element|null}
     * @private
     */
    #activeElementReset = null;

    /**
     * Begins trapping focus changes using the keyboard to only elements within the current dialog.
     */
    trapFocus() {
        this.#activeElementReset = document.activeElement ?? null;
        this.element.addEventListener( "keydown", this.onTrapFocusKeyDown );
    }

    /**
     * Releases the trapping of focus changes back to the document.
     */
    releaseFocus() {
        this.element.removeEventListener( "keydown", this.onTrapFocusKeyDown );
        if ( this.#activeElementReset instanceof HTMLElement ){
            this.#activeElementReset.focus();
            this.#activeElementReset = null;
        }
    }

    /**
     * Handle the Tab keydown event to ensure the focus remains trapped within the dialog.
     * @param {KeyboardEvent} event
     */
    onTrapFocusKeyDown( event ) {
        if ( event.key === "Tab" ) {
            const boundary = event.shiftKey ? this.buttonClose : this.buttonCancel;
            if ( event.target === boundary ) {
                event.preventDefault();
                const redirect = event.shiftKey ? this.buttonCancel : this.buttonClose;
                redirect.focus();
            }
        }
        if ( event.key === "Escape" ) {
            event.preventDefault();
            this.close();
        }
    }

    //endregion
};